import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Calendar } from './calendar'

function pad(value) {
  return String(value).padStart(2, '0')
}

function formatIsoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function parseIsoDate(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const [year, month, day] = value.split('-').map(Number)
  const parsed = new Date(year, month - 1, day)
  if (Number.isNaN(parsed.getTime())) return null
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null
  }
  return parsed
}

function displayDate(value) {
  const date = parseIsoDate(value)
  if (!date) return ''
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`
}

function isOutOfRange(date, minDate, maxDate) {
  const iso = formatIsoDate(date)
  if (minDate && iso < minDate) return true
  if (maxDate && iso > maxDate) return true
  return false
}

// Parse a dd-mm-yyyy typed string into an ISO date string (yyyy-mm-dd)
function parseDdMmYyyy(text) {
  if (!text) return null
  const clean = text.replace(/[^\d-]/g, '')
  // Accept both dd-mm-yyyy and dd/mm/yyyy
  const match = clean.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/)
  if (!match) return null
  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2200) return null
  const d = new Date(year, month - 1, day)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null
  return formatIsoDate(d)
}

function formatAsYouType(val) {
  // Strip all non-digit characters
  const digits = val.replace(/\D/g, '')

  if (digits.length <= 2) {
    return digits
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`
  }
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`
}

export default function AppDateInput({
  value,
  onChange,
  disabled = false,
  className = '',
  inputClassName = '',
  min,
  max,
  name,
  id,
  title,
  iconClassName = '',
  buttonClassName = '',
  placeholder = 'dd-mm-yyyy',
}) {
  const wrapperRef = useRef(null)
  const panelRef = useRef(null)
  const selectedDate = useMemo(() => parseIsoDate(value), [value])
  const today = useMemo(() => new Date(), [])
  const [isOpen, setIsOpen] = useState(false)
  const [monthCursor, setMonthCursor] = useState(() => selectedDate || today)
  const [panelStyle, setPanelStyle] = useState({ top: 0, left: 0, width: 272 })
  // Local text state for manual typing
  const [typedText, setTypedText] = useState(null) // null = not typing; string = user is typing

  useEffect(() => {
    if (!selectedDate) return
    setMonthCursor(selectedDate)
  }, [selectedDate])

  useEffect(() => {
    if (!isOpen) return undefined
    const onPointerDown = (event) => {
      const clickedInsideTrigger = wrapperRef.current?.contains(event.target)
      const clickedInsidePanel = panelRef.current?.contains(event.target)
      if (!clickedInsideTrigger && !clickedInsidePanel) {
        setIsOpen(false)
      }
    }
    window.addEventListener('pointerdown', onPointerDown)
    return () => window.removeEventListener('pointerdown', onPointerDown)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined

    const computePanelPosition = () => {
      const triggerRect = wrapperRef.current?.getBoundingClientRect()
      if (!triggerRect) return

      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      const desiredWidth = 284
      const panelHeight = Math.max(320, panelRef.current?.offsetHeight || 340)
      const gap = 6

      // Vertical positioning: default directly below trigger; flip above if not enough space below
      const spaceBelow = viewportHeight - triggerRect.bottom - gap
      const spaceAbove = triggerRect.top - gap
      const openAbove = spaceBelow < panelHeight && spaceAbove > spaceBelow
      const top = openAbove ? triggerRect.top - panelHeight - gap : triggerRect.bottom + gap

      // Horizontal positioning: align with left of trigger, clamp to viewport margins
      let left = triggerRect.left
      if (left + desiredWidth > viewportWidth - 12) {
        left = viewportWidth - desiredWidth - 12
      }
      left = Math.max(12, left)

      setPanelStyle({
        top: Math.round(Math.max(8, top)),
        left: Math.round(left),
        width: desiredWidth,
      })
    }

    computePanelPosition()
    window.addEventListener('resize', computePanelPosition)
    window.addEventListener('scroll', computePanelPosition, true)
    return () => {
      window.removeEventListener('resize', computePanelPosition)
      window.removeEventListener('scroll', computePanelPosition, true)
    }
  }, [isOpen])

  const openPicker = () => {
    if (disabled) return
    setIsOpen(true)
  }

  const selectDate = (date) => {
    if (disabled) return
    if (isOutOfRange(date, min, max)) return
    onChange?.(formatIsoDate(date))
    setTypedText(null)
    setIsOpen(false)
  }

  const clearDate = () => {
    if (disabled) return
    onChange?.('')
    setTypedText(null)
    setIsOpen(false)
  }

  const jumpToday = () => {
    setMonthCursor(today)
    if (!disabled && !isOutOfRange(today, min, max)) {
      onChange?.(formatIsoDate(today))
      setTypedText(null)
      setIsOpen(false)
    }
  }

  // Commit typed text: parse dd-mm-yyyy and call onChange
  const commitTypedText = () => {
    if (typedText === null) return
    const trimmed = typedText.trim()
    if (!trimmed) {
      // Clear
      onChange?.('')
      setTypedText(null)
      return
    }
    const iso = parseDdMmYyyy(trimmed)
    if (iso && (!min || iso >= min) && (!max || iso <= max)) {
      onChange?.(iso)
    }
    // Revert display regardless (valid or not)
    setTypedText(null)
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <input
        id={id}
        name={name}
        type="text"
        value={typedText !== null ? typedText : displayDate(value)}
        disabled={disabled}
        title={title}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          if (disabled) return
          const formatted = formatAsYouType(e.target.value)
          setTypedText(formatted)
          setIsOpen(false)
        }}
        onFocus={() => {
          if (disabled) return
          // Start typing: seed with current display value so cursor is at end
          if (typedText === null) setTypedText(displayDate(value))
        }}
        onBlur={commitTypedText}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commitTypedText()
          } else if (e.key === 'Escape') {
            setTypedText(null)
          }
        }}
        className={`w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2.5 pr-10 text-sm font-medium text-on-surface shadow-sm outline-none transition hover:border-primary/35 focus:ring-2 focus:ring-primary/25 disabled:cursor-not-allowed disabled:opacity-60 [&::-webkit-calendar-picker-indicator]:opacity-0 ${inputClassName}`}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-on-surface-variant transition hover:bg-surface-container-low hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
        aria-label="Open calendar"
      >
        <span className={`material-symbols-outlined text-base ${iconClassName}`}>calendar_month</span>
      </button>

      {isOpen && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={panelRef}
            style={{ top: panelStyle.top, left: panelStyle.left, width: panelStyle.width }}
            className="fixed z-[560] max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-3 shadow-[0_16px_40px_rgba(15,23,42,0.18)] backdrop-blur-xl flex flex-col"
          >
            <Calendar
              selected={selectedDate}
              onSelect={selectDate}
              month={monthCursor}
              onMonthChange={setMonthCursor}
              min={min}
              max={max}
              onClear={clearDate}
              onToday={jumpToday}
              className="w-full"
            />
          </div>,
          document.body,
        )
        : null}
    </div>
  )
}
